import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api.ts";
import type { CustomerDocType, CustomerOut } from "@/types/api.ts";

export const customerKeys = {
  all: ["admin", "customers"] as const,
  list: (q: string) => [...customerKeys.all, "list", q] as const,
};

interface ListVars {
  q?: string;
  limit?: number;
  offset?: number;
}

/** doc_type/doc_number son inmutables: van solo en POST. PATCH no los acepta. */
export interface CustomerCreateInput {
  doc_type: CustomerDocType;
  doc_number: string;
  full_name: string;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  is_active: boolean;
}

export interface CustomerUpdateInput {
  full_name?: string;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  is_active?: boolean;
}

export function useAdminCustomers({ q = "", limit = 50, offset = 0 }: ListVars) {
  return useQuery({
    queryKey: customerKeys.list(q),
    queryFn: () =>
      api.get<CustomerOut[]>("/customers", {
        searchParams: { q: q || undefined, limit, offset },
      }),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerCreateInput) => api.post<CustomerOut>("/customers", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

export function useUpdateCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerUpdateInput) =>
      api.patch<CustomerOut>(`/customers/${customerId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => api.del<void>(`/customers/${customerId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}
